import React from 'react';
import { Container, Typography, Box, Paper, Stack, Divider, Chip, useMediaQuery } from '@mui/material';
import {
  CloudUpload, BarChart, Dashboard, Category, AccountBalance,
  FileDownload, CreditCard, Description, CardGiftcard, ContentCut,
} from '@mui/icons-material';

import { ANALYZER_MAX_PAGES_PER_FILE } from './uploadHelpText';
import MobileAppCta from './MobileAppCta';

const SectionHeader = ({ icon, title, subtitle }) => (
  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
    <Box sx={{
      width: 48, height: 48, borderRadius: '50%', backgroundColor: 'primary.main',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      {React.cloneElement(icon, { sx: { color: '#fff', fontSize: 26 } })}
    </Box>
    <Box>
      <Typography variant="h5" fontWeight={700}>{title}</Typography>
      {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
    </Box>
  </Stack>
);

const Step = ({ number, children }) => (
  <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
    <Box sx={{
      width: 28, height: 28, borderRadius: '50%', backgroundColor: 'primary.light',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.25,
    }}>
      <Typography variant="caption" fontWeight={700} color="primary.dark">{number}</Typography>
    </Box>
    <Typography variant="body1" sx={{ flex: 1 }}>{children}</Typography>
  </Stack>
);

export default function HowItWorks() {
  const isMobile = useMediaQuery('(max-width:600px)');

  return (
    <Container maxWidth="md" sx={{ py: isMobile ? 3 : 6 }}>
      <Box sx={{ textAlign: 'center', mb: 5 }}>
        <Typography variant="h3" fontWeight={800} color="primary.main" gutterBottom>
          How It Works
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          A complete guide to analyzing your mobile money statements, managing your dashboard, and getting the most out of every feature.
        </Typography>
      </Box>

      <MobileAppCta compact />

      {/* ── 1. Getting Started ──────────────────────────────────────────── */}
      <Paper sx={{ p: isMobile ? 2 : 4, mb: 4 }}>
        <SectionHeader icon={<CloudUpload />} title="1. Getting Started" subtitle="Choose Full Analysis, Convert, or Split PDF—then upload and run" />
        <Divider sx={{ mb: 2 }} />

        <Typography variant="body1" paragraph>
          The analyzer page is the first thing you see. It supports <strong>M-PESA</strong>, <strong>KCB</strong>, <strong>Airtel Money</strong>, and <strong>Equity Bank</strong> PDF statements.
          {' '}
          For <strong>Full Analysis</strong> and <strong>Convert</strong>, you can upload one or multiple PDFs at once.
          {' '}
          Each PDF is read up to about <strong>{ANALYZER_MAX_PAGES_PER_FILE} pages per upload</strong> in one run (a server safety limit). Longer statements may look incomplete unless you split them first.
          {' '}
          Use <strong>Split PDF</strong> right on the Analyzer (same toggle row as Full Analysis and Convert) to download a ZIP of smaller PDFs—default <strong>{ANALYZER_MAX_PAGES_PER_FILE} pages per file</strong>, aligned with that limit.
          {' '}
          Splitting does <strong>not</strong> use your analysis page quota. You can also open the standalone <Box component="a" href="/split-pdf">Split PDF page</Box> if you prefer.
          {' '}
          After splitting, unzip the files and upload each part with Full Analysis or Convert; your normal page quota applies to those uploads.
        </Typography>

        <Step number="1">
          Choose a mode at the top: <strong>Full Analysis</strong>, <strong>Convert</strong>, or <strong>Split PDF</strong>.
        </Step>

        <Stack direction="row" spacing={1} sx={{ ml: 5, mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Chip label="Full Analysis" variant="outlined" color="primary" size="small" />
          <Chip label="Convert (Excel / CSV)" variant="outlined" size="small" />
          <Chip icon={<ContentCut sx={{ fontSize: '18px' }} />} label="Split PDF" variant="outlined" size="small" />
        </Stack>

        <Typography variant="body1" paragraph>
          <strong>Full Analysis</strong> processes the data, generates charts, and calculates totals.{' '}
          <strong>Convert</strong> extracts table data from the PDF and downloads <strong>Excel</strong> or <strong>CSV</strong> with dates and amounts formatted for spreadsheets (no charts).{' '}
          <strong>Split PDF</strong> takes <strong>one</strong> PDF and returns a ZIP of parts (optional password; pages-per-file is configurable). Later parts repeat the first page so statement headers stay present—duplicate header rows are handled when you combine or deduplicate results.
        </Typography>

        <Step number="2">
          Upload PDFs: several files for Full Analysis or Convert, or <strong>one</strong> PDF for Split PDF (max 100MB). Enter the password if the file is protected.
        </Step>

        <Step number="3">
          <strong>Full Analysis:</strong> optionally set period, date range, min/max amounts, and paid type (Withdrawn / Paid In).{' '}
          <strong>Convert:</strong> choose Excel or CSV.{' '}
          <strong>Split PDF:</strong> adjust pages per file if needed (default {ANALYZER_MAX_PAGES_PER_FILE}).
        </Step>

        <Step number="4">
          Click <strong>Analyze Statement</strong>, <strong>Convert to …</strong>, or <strong>Download ZIP of split PDFs</strong>, then wait for processing or download.
        </Step>

        <Box component="img" src="/how_it_works/Analyzer_page.png" sx={{ width: '100%', borderRadius: 2, my: 2 }} />

        <Typography variant="body2" color="text.secondary">
          Tip: Want to save and track analyses over time? Use the Dashboard to upload and manage your statements.
        </Typography>
      </Paper>

      {/* ── 2. Understanding Your Analysis Results ──────────────────────── */}
      <Paper sx={{ p: isMobile ? 2 : 4, mb: 4 }}>
        <SectionHeader icon={<BarChart />} title="2. Understanding Your Analysis Results" subtitle="Charts, averages, and entity breakdowns" />
        <Divider sx={{ mb: 2 }} />

        <Typography variant="body1" paragraph>
          After analysis completes, you'll see a stacked bar chart showing your top 10 entities (people or businesses) by total amount, broken down by day.
        </Typography>

        <Typography variant="subtitle2" fontWeight={600} gutterBottom>Chart Granularity</Typography>
        <Typography variant="body1" paragraph>
          Use the <strong>Daily | Monthly | Yearly</strong> toggle above the chart to change the time grouping. Daily shows each day as a bar, Monthly aggregates into calendar months, and Yearly sums the entire year into one bar. This helps you spot spending trends at different zoom levels.
        </Typography>

        <Box component="img" src="/how_it_works/Stacked_Chart.png" sx={{ width: '100%', borderRadius: 2, my: 2 }} />

        <Typography variant="subtitle2" fontWeight={600} gutterBottom>Averages</Typography>
        <Typography variant="body1" paragraph>
          Below the chart you'll see three key metrics: <strong>Daily Average</strong>, <strong>Weekly Average</strong>, and <strong>Monthly Average</strong>. These are calculated from the filtered data, so they update when you apply date or amount filters.
        </Typography>

        <Typography variant="subtitle2" fontWeight={600} gutterBottom>Top Entities Table</Typography>
        <Typography variant="body1" paragraph>
          Below the chart is a searchable, sortable table listing every person or business in your statement. Use the search bar to find specific entities, or select multiple entities to highlight them in the chart.
        </Typography>

        <Box component="img" src="/how_it_works/Top_entities.png" sx={{ width: '100%', borderRadius: 2, my: 2 }} />
      </Paper>

      {/* ── 3. Dashboard Overview ───────────────────────────────────────── */}
      <Paper sx={{ p: isMobile ? 2 : 4, mb: 4 }}>
        <SectionHeader icon={<Dashboard />} title="3. Dashboard Overview" subtitle="Upload, save, and manage your analyses" />
        <Divider sx={{ mb: 2 }} />

        <Typography variant="body1" paragraph>
          The Dashboard is where you save and track your analyses over time. You need a free account to access it. Unlike the Analyzer (which shows one-off results), the Dashboard stores your data so you can revisit it, combine multiple statements, and build a complete picture of your finances.
        </Typography>

        <Step number="1">Go to the Dashboard tab and upload your PDF statement(s).</Step>
        <Step number="2">Your analysis is saved automatically. Charts, summaries, and entity lists appear right away.</Step>
        <Step number="3">Upload more PDFs anytime. New uploads can be combined with your existing data or saved as a separate analysis.</Step>

        <Box component="img" src="/how_it_works/Dashboard_page.png" sx={{ width: '100%', borderRadius: 2, my: 2 }} />

        <Typography variant="subtitle2" fontWeight={600} gutterBottom>Personal vs Business View</Typography>
        <Typography variant="body1" paragraph>
          Toggle between <strong>Personal</strong> and <strong>Business</strong> views at the top of the dashboard. Personal view shows your spending with categories like Food, Transport, and Utilities. Business view provides expense and income categories relevant to businesses, plus a Profit/Loss summary.
        </Typography>

        <Typography variant="subtitle2" fontWeight={600} gutterBottom>Combining Statements</Typography>
        <Typography variant="body1" paragraph>
          When uploading a new statement, you can choose to <strong>combine with existing data</strong>. This merges transactions, automatically removes duplicates, and gives you a unified view across multiple statements or time periods.
        </Typography>

        <Box component="img" src="/how_it_works/Personal_v_Business.png" sx={{ width: '100%', borderRadius: 2, my: 2 }} />
      </Paper>

      {/* ── 4. Tagging and Categories ───────────────────────────────────── */}
      <Paper sx={{ p: isMobile ? 2 : 4, mb: 4 }}>
        <SectionHeader icon={<Category />} title="4. Tagging and Categories" subtitle="Organize transactions by category for deeper insights" />
        <Divider sx={{ mb: 2 }} />

        <Typography variant="body1" paragraph>
          Tagging lets you assign categories (like "Rent", "Groceries", "Salary") to each person or business in your transactions. Once tagged, the dashboard shows a category breakdown with totals, percentages, and a pie chart.
        </Typography>

        <Step number="1">In the Dashboard, find the entity you want to tag in the entities list.</Step>
        <Step number="2">Click the edit icon next to the entity name.</Step>
        <Step number="3">Select a predefined category from the dropdown, or type a custom category name.</Step>
        <Step number="4">Click Save. The category breakdown updates immediately.</Step>

        <Box component="img" src="/how_it_works/Tagging.png" sx={{ width: '100%', borderRadius: 2, my: 2 }} />

        <Typography variant="subtitle2" fontWeight={600} gutterBottom>Auto-Tagging</Typography>
        <Typography variant="body1" paragraph>
          The system auto-tags entities in two ways. First, it recognizes keywords in entity names; for example, entities containing words like "supermarket", "naivas", or "carrefour" are automatically tagged as <strong>Shopping</strong>, while "hospital", "pharmacy", or "clinic" are tagged as <strong>Healthcare</strong>. This covers common categories like Food &amp; Dining, Transportation, Airtime, Utilities, and many more out of the box.
        </Typography>
        <Typography variant="body1" paragraph>
          Second, once you manually tag an entity, the system remembers it. When you upload new statements containing the same entity, it's automatically tagged with the same category. Manual tags always take priority over keyword-based tags, so you're always in control.
        </Typography>

        <Typography variant="subtitle2" fontWeight={600} gutterBottom>Category Breakdown</Typography>
        <Typography variant="body1" paragraph>
          The category list shows each category sorted by total amount in descending order, with a percentage bar and entity count. Click any category to expand it and see all entities tagged under it, also sorted by amount.
        </Typography>

        <Box component="img" src="/how_it_works/Category_breakdown.png" sx={{ width: '100%', borderRadius: 2, my: 2 }} />
      </Paper>

      {/* ── 5. Financial Summaries ──────────────────────────────────────── */}
      <Paper sx={{ p: isMobile ? 2 : 4, mb: 4 }}>
        <SectionHeader icon={<AccountBalance />} title="5. Financial Summaries" subtitle="At-a-glance totals for personal and business use" />
        <Divider sx={{ mb: 2 }} />

        <Typography variant="body1" paragraph>
          At the top of the dashboard, a summary card shows your key financial metrics. The labels change based on your selected view:
        </Typography>

        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body1"><strong>Personal view:</strong> Paid In, Withdrawn, and Balance</Typography>
          <Typography variant="body1"><strong>Business view:</strong> Income, Expenses, and Net Profit / Loss</Typography>
        </Box>

        <Typography variant="body1" paragraph>
          These figures are calculated from the <em>full unfiltered dataset</em>, so they always reflect the true totals regardless of which view mode (Withdrawn / Paid In) you have selected for the chart.
        </Typography>

        <Box component="img" src="/how_it_works/Summary_card.png" sx={{ width: '100%', borderRadius: 2, my: 2 }} />
      </Paper>

      {/* ── 6. Exporting Your Data ──────────────────────────────────────── */}
      <Paper sx={{ p: isMobile ? 2 : 4, mb: 4 }}>
        <SectionHeader icon={<FileDownload />} title="6. Exporting Your Data" subtitle="Download your results in multiple formats" />
        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" fontWeight={600} gutterBottom>Export to Excel</Typography>
        <Typography variant="body1" paragraph>
          Both the Analyzer and Dashboard have an <strong>Export to Excel</strong> button. This downloads an <code>.xlsx</code> workbook with <strong>typed</strong> date and amount columns (spreadsheet-ready), plus a sheet for top entities.
        </Typography>

        <Typography variant="subtitle2" fontWeight={600} gutterBottom>Download chart</Typography>
        <Typography variant="body1" paragraph>
          On the Dashboard, click the download icon near the chart to save it as a PNG image. This is useful for reports and presentations.
        </Typography>

        <Typography variant="subtitle2" fontWeight={600} gutterBottom>Raw PDF conversion</Typography>
        <Typography variant="body1" paragraph>
          Use <strong>Convert</strong> on the Analyzer to extract table data without running charts or summaries. Choose <strong>Excel</strong> or <strong>CSV</strong>; known date and amount columns are formatted for Excel or other tools instead of plain text where possible.
        </Typography>

        <Typography variant="subtitle2" fontWeight={600} gutterBottom>Category Import / Export</Typography>
        <Typography variant="body1" paragraph>
          On the Dashboard, you can export your category tags to Excel and import them back later. This is useful for backing up your tags or transferring them between accounts.
        </Typography>

        <Box component="img" src="/how_it_works/Export_buttons.png" sx={{ width: '100%', borderRadius: 2, my: 2 }} />
      </Paper>

      {/* ── 7. Plans and Page Limits ────────────────────────────────────── */}
      <Paper sx={{ p: isMobile ? 2 : 4, mb: 4 }}>
        <SectionHeader icon={<CreditCard />} title="7. Plans and Page Limits" subtitle="Understand your usage quotas and purchase options" />
        <Divider sx={{ mb: 2 }} />

        <Typography variant="body1" paragraph>
          Every upload consumes pages from your quota. Here's how quotas work:
        </Typography>

        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body1" gutterBottom><strong>Guest (not logged in):</strong> 5 pages per 24 hours</Typography>
          <Typography variant="body1" gutterBottom><strong>Free account:</strong> 20 pages per 24 hours, up to 100 pages per 30 days</Typography>
          <Typography variant="body1" gutterBottom><strong>Day Pass:</strong> A one-time purchase that gives you a fixed number of extra pages valid for 24 hours</Typography>
          <Typography variant="body1" gutterBottom><strong>30-day plans:</strong> Starter (500 pages), Professional (2,000 pages), Business (10,000 pages). Each is a one-time purchase and does not auto-renew.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            <strong>Large statements:</strong> Your 30-day or Day Pass allowance counts pages across all uploads. Full Analysis and Convert each read at most about <strong>{ANALYZER_MAX_PAGES_PER_FILE} pages per PDF</strong> in one run; split bigger files first using <strong>Split PDF</strong> on the Analyzer or the{' '}
            <Box component="a" href="/split-pdf">standalone Split PDF page</Box>, then upload each part.
          </Typography>
        </Box>

        <Typography variant="body1" paragraph>
          A free account includes 20 pages per 24 hours, capped at 100 pages per 30 days, and lets you save analyses. If you need more, buy a Day Pass or a one-time 30-day page plan.
        </Typography>

        <Box component="img" src="/how_it_works/Subscription_page.png" sx={{ width: '100%', borderRadius: 2, my: 2 }} />
      </Paper>

      {/* ── 8. Referral Program ──────────────────────────────────────────── */}
      <Paper sx={{ p: isMobile ? 2 : 4, mb: 4 }}>
        <SectionHeader icon={<CardGiftcard />} title="8. Referral Program" subtitle="Invite friends and earn bonus pages" />
        <Divider sx={{ mb: 2 }} />

        <Typography variant="body1" paragraph>
          You can earn extra pages by inviting other users to join the platform. Your referral link is available on the <strong>Plans</strong> tab after you log in.
        </Typography>

        <Step number="1">Open the <strong>Plans</strong> tab and copy your referral link or referral code.</Step>
        <Step number="2">Share the link with a friend. When they sign up through that link, the referral code is saved automatically.</Step>
        <Step number="3">After they confirm their account and sign in for the first time, you receive <strong>100 bonus pages</strong>.</Step>
        <Step number="4">When that same user buys their first paid 30-day plan, you receive <strong>500 more bonus pages</strong> and they also receive <strong>500 bonus pages</strong>.</Step>

        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body1" gutterBottom><strong>Signup reward:</strong> Referrer gets 100 pages</Typography>
          <Typography variant="body1" gutterBottom><strong>First paid plan reward:</strong> Referrer gets 500 pages</Typography>
          <Typography variant="body1"><strong>Referred user reward:</strong> Gets 500 pages after their first paid 30-day plan</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary">
          Referral rewards are designed to stack with your normal page limits, so bonus pages can be used alongside free, day-pass, or paid-plan access.
        </Typography>
      </Paper>

      {/* ── 9. Supported Statement Types ────────────────────────────────── */}
      <Paper sx={{ p: isMobile ? 2 : 4, mb: 4 }}>
        <SectionHeader icon={<Description />} title="9. Supported Statement Types" subtitle="PDF statements we can analyze" />
        <Divider sx={{ mb: 2 }} />

        <Typography variant="body1" paragraph>
          The analyzer automatically detects your statement type from its column headers. The following formats are supported:
        </Typography>

        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {[
            { name: 'M-PESA', cols: 'Receipt No, Completion Time, Details, Transaction Status, Paid In, Withdrawn, Balance' },
            { name: 'KCB', cols: 'Date, Value Date, Description, Debit, Credit, Balance' },
            { name: 'Airtel Money', cols: 'Transaction ID, Transaction Date, Description, Status, Credit, Debit, Balance' },
            { name: 'Equity Bank', cols: 'Date, Value, Particulars, Money Out, Money In, Balance' },
          ].map((s) => (
            <Box key={s.name} sx={{ p: 2, backgroundColor: '#f9f9f9', borderRadius: 1, borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
              <Typography variant="subtitle2" fontWeight={700}>{s.name}</Typography>
              <Typography variant="body2" color="text.secondary">{s.cols}</Typography>
            </Box>
          ))}
        </Stack>

        <Typography variant="body1" paragraph>
          If your statement has a different format, the analyzer will attempt to detect the headers automatically. Columns are mapped to standardized names internally, so the analysis output is consistent regardless of the source.
        </Typography>
      </Paper>

      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Have questions or feedback? Use the chat icon to send us a message.
        </Typography>
      </Box>
    </Container>
  );
}
